import React, { useState, useMemo, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, FileDown, ArrowRight, Calendar, Tag, Filter, Eye, Play, Clock, MapPin, Video } from "lucide-react";
import { BLOG_CATEGORIES, VIDEOS } from "../lib/content/insights";
import { usePublishedContent } from "../lib/content/published";
import SEO, { videoFeedLd } from "../components/SEO";
import VideoLightbox from "../components/hidroverse/VideoLightbox";

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" });

export default function Insights() {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [tab, setTab] = useState("posts");
  const [activeVideo, setActiveVideo] = useState(null);
  const location = useLocation();
  const { posts: blendedPosts, resources: blendedResources } = usePublishedContent();

  // Honour ?tab=videos deeplinks from the Home strip + footer.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const next = params.get("tab");
    if (next && ["posts", "resources", "videos"].includes(next)) setTab(next);
  }, [location.search]);

  const filteredPosts = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return blendedPosts.filter((p) => {
      if (cat !== "all" && p.category !== cat) return false;
      if (!needle) return true;
      const hay = `${p.title} ${p.excerpt} ${(p.tags || []).join(" ")} ${p.author || ""}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [q, cat, blendedPosts]);

  const filteredResources = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return blendedResources.filter((r) => {
      if (cat !== "all" && r.category !== cat) return false;
      if (!needle) return true;
      return `${r.title} ${r.body} ${(r.tags || []).join(" ")}`.toLowerCase().includes(needle);
    });
  }, [q, cat, blendedResources]);

  const filteredVideos = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const filtered = VIDEOS.filter((v) => {
      if (cat !== "all" && v.category !== cat) return false;
      if (!needle) return true;
      const hay = `${v.title} ${v.caption} ${(v.tags || []).join(" ")} ${v.filmed_at || ""}`.toLowerCase();
      return hay.includes(needle);
    });
    // Pinned (home_strip:true) first, then the rest sorted by date desc.
    const pinned = filtered.filter((v) => v.home_strip);
    const rest = filtered
      .filter((v) => !v.home_strip)
      .sort((a, b) => (a.date < b.date ? 1 : -1));
    return [...pinned, ...rest];
  }, [q, cat]);

  return (
    <div data-testid="insights-page">
      <SEO
        title="Insights"
        description="Factual, referenced water-engineering insights and pragmatic PDF resources for lodge GMs, estate trustees and municipal engineers. New post every Monday."
        path="/insights"
        ldJson={videoFeedLd(VIDEOS)}
      />

      {/* HERO */}
      <section
        className="px-6 md:px-12 lg:px-20 pt-32 pb-12 md:pb-16"
        style={{ background: "#FAFAF7", color: "#0C2A40" }}
      >
        <div className="max-w-[1400px] mx-auto">
          <h1
            className="font-display"
            style={{
              fontSize: "clamp(2.4rem, 5.6vw, 4.8rem)",
              lineHeight: 1.04,
              letterSpacing: "-0.02em",
              fontWeight: 400,
            }}
          >
            Factual water engineering.<br />
            <span style={{ color: "#1FB6C1" }}>Published weekly.</span>
          </h1>
          <p className="mt-7 max-w-2xl text-[1.02rem] font-light text-black/72 leading-relaxed">
            Engineering essays, regulatory primers and pragmatic PDF tools for lodge GMs, estate
            trustees, municipal engineers and procurement officers. Always factual.
            Always referenced. New post every Monday.
          </p>
        </div>
      </section>

      {/* CONTROLS BAR */}
      <section
        className="px-6 md:px-12 lg:px-20 sticky top-0 z-40 border-y backdrop-blur-md"
        style={{ background: "rgba(255,255,255,0.92)", borderColor: "rgba(0,0,0,0.08)" }}
        data-testid="insights-controls"
      >
        <div className="max-w-[1400px] mx-auto py-4 flex flex-col md:flex-row gap-3 md:items-center">
          {/* Tabs */}
          <div
            className="inline-flex items-center gap-1 p-1 rounded-full"
            style={{ background: "#F4F5F2", border: "1px solid rgba(0,0,0,0.06)" }}
            data-testid="insights-tabs"
          >
            {[
              ["posts", `Articles (${blendedPosts.length})`],
              ["resources", `Downloads (${blendedResources.length})`],
              ["videos", `Videos${VIDEOS.length ? ` (${VIDEOS.length})` : ""}`],
            ].map(([k, label]) => (
              <button
                key={k}
                type="button"
                onClick={() => setTab(k)}
                className="px-4 py-1.5 transition-colors"
                style={{
                  background: tab === k ? "#0C2A40" : "transparent",
                  color: tab === k ? "#FFFFFF" : "#0C2A40",
                  borderRadius: 999,
                  fontSize: "0.74rem",
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  fontWeight: 600,
                }}
                data-testid={`insights-tab-${k}`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="flex-1 relative" data-testid="insights-search-wrap">
            <Search size={15} aria-hidden style={{ position: "absolute", left: 14, top: 13, color: "rgba(12,42,64,0.45)" }} />
            <input
              type="search"
              placeholder="Search by title, keyword or technology…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 text-[0.92rem] font-light outline-none transition-colors"
              style={{
                background: "#FFFFFF",
                border: "1px solid rgba(0,0,0,0.10)",
                borderRadius: 999,
                color: "#0C2A40",
              }}
              data-testid="insights-search"
            />
          </div>

          {/* Category filter */}
          <div className="flex items-center gap-2">
            <Filter size={13} aria-hidden style={{ color: "rgba(12,42,64,0.55)" }} />
            <select
              value={cat}
              onChange={(e) => setCat(e.target.value)}
              className="px-3 py-2.5 text-[0.82rem] font-medium outline-none"
              style={{
                background: "#FFFFFF",
                border: "1px solid rgba(0,0,0,0.10)",
                borderRadius: 999,
                color: "#0C2A40",
              }}
              data-testid="insights-category-select"
            >
              {BLOG_CATEGORIES.map((c) => (
                <option key={c.slug} value={c.slug}>{c.label}</option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="px-6 md:px-12 lg:px-20 py-12 md:py-16" style={{ background: "#FFFFFF" }}>
        <div className="max-w-[1400px] mx-auto">
          {tab === "posts" && (
            <>
              {filteredPosts.length === 0 ? (
                <p className="text-black/50 font-light text-center py-16" data-testid="insights-empty">
                  No posts match your search.
                </p>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7" data-testid="insights-posts-grid">
                  {filteredPosts.map((p, i) => (
                    <Link
                      key={p.slug}
                      to={`/insights/${p.slug}`}
                      className="group block"
                      data-testid={`post-card-${p.slug}`}
                    >
                      <div
                        className="aspect-[16/9] md:aspect-[16/10] overflow-hidden mb-4"
                        style={{ background: "#F4F5F2", borderRadius: 4 }}
                      >
                        <img
                          src={p.cover}
                          alt=""
                          loading="lazy"
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          onError={(e) => { e.currentTarget.src = "/library/purification/purification_01.webp"; }}
                        />
                      </div>
                      <div
                        className="mb-2 inline-flex items-center gap-2 text-[0.78rem] font-medium"
                        style={{ color: "rgba(12,42,64,0.55)" }}
                      >
                        <Calendar size={11} aria-hidden /> {fmtDate(p.date)} · {p.readMinutes} min read
                      </div>
                      <h3
                        className="font-display group-hover:text-[#1FB6C1] transition-colors"
                        style={{
                          fontSize: "1.18rem",
                          lineHeight: 1.25,
                          letterSpacing: "-0.01em",
                          color: "#0C2A40",
                          fontWeight: 600,
                        }}
                      >
                        {p.title}
                      </h3>
                      <p className="mt-3 font-light text-[0.92rem] leading-relaxed text-black/65 line-clamp-3">
                        {p.excerpt}
                      </p>
                      <div className="mt-4 flex flex-wrap gap-1.5">
                        {p.tags.slice(0, 3).map((t) => (
                          <span
                            key={t}
                            className="inline-flex items-center px-2 py-0.5 text-[0.62rem] font-medium"
                            style={{
                              background: "rgba(12,42,64,0.06)",
                              color: "rgba(12,42,64,0.7)",
                              borderRadius: 3,
                              letterSpacing: "0.04em",
                            }}
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </>
          )}

          {tab === "resources" && (
            <>
              {filteredResources.length === 0 ? (
                <p className="text-black/50 font-light text-center py-16">No downloads match your search.</p>
              ) : (
                <div className="grid md:grid-cols-2 gap-5 md:gap-6" data-testid="insights-resources-grid">
                  {filteredResources.map((r) => (
                    <ResourceCard key={r.slug} resource={r} />
                  ))}
                </div>
              )}
            </>
          )}

          {tab === "videos" && (
            <>
              {VIDEOS.length === 0 ? (
                <VideosEmptyState />
              ) : filteredVideos.length === 0 ? (
                <p className="text-black/50 font-light text-center py-16" data-testid="insights-videos-empty">
                  No videos match your search.
                </p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4" data-testid="insights-videos-grid">
                  {filteredVideos.map((v) => (
                    <VideoCard key={v.slug} video={v} onPlay={() => setActiveVideo(v)} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>
      <VideoLightbox video={activeVideo} onClose={() => setActiveVideo(null)} />
    </div>
  );
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api/hidroverse/leadmagnet`;

function ResourceCard({ resource: r }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [state, setState] = useState({ status: "idle", msg: "", url: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setState({ status: "error", msg: "Enter a valid email address.", url: "" });
      return;
    }
    setState({ status: "loading", msg: "", url: "" });
    try {
      const res = await fetch(API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, magnet: r.slug, source: "insights-hub" }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      // After capture, send the user straight to the in-app PDF preview.
      // This keeps PWA users inside our shell (with a real back button)
      // and gives a Download button on the preview page itself.
      navigate(`/insights/resource/${r.slug}`);
      setState({ status: "success", msg: "Your PDF is ready.", url: data.download_url || "" });
    } catch {
      setState({ status: "error", msg: "Something went wrong — try again.", url: "" });
    }
  };

  const filename = `hidroverse-${r.slug}.pdf`;

  return (
    <div
      className="p-7"
      style={{
        background: "#FFFFFF",
        border: "1px solid rgba(12,42,64,0.08)",
        borderRadius: 4,
        boxShadow: "0 2px 14px rgba(12,42,64,0.05)",
      }}
      data-testid={`resource-card-${r.slug}`}
    >
      <div className="flex items-start gap-4 mb-4">
        <div
          className="w-10 h-10 rounded-md flex items-center justify-center shrink-0"
          style={{ background: "rgba(31,182,193,0.10)", color: "#1FB6C1" }}
        >
          <FileDown size={18} />
        </div>
        <div>
          <div
            className="mb-1.5 text-[0.78rem] font-medium"
            style={{ color: "rgba(12,42,64,0.55)" }}
          >
            Free PDF · {r.pages} pp · No mailing list
          </div>
          <div
            className="font-display"
            style={{ fontSize: "1.1rem", lineHeight: 1.25, fontWeight: 600, color: "#0C2A40", letterSpacing: "-0.005em" }}
          >
            {r.title}
          </div>
        </div>
      </div>
      <p className="font-light text-[0.9rem] leading-relaxed text-black/70 mb-5">{r.body}</p>

      {state.status === "success" ? (
        <div
          className="px-4 py-4 rounded-md"
          style={{ background: "rgba(66,149,75,0.10)", color: "#1F6B27", border: "1px solid rgba(66,149,75,0.25)" }}
          data-testid={`resource-success-${r.slug}`}
        >
          <div className="text-[0.92rem] font-semibold mb-3">{state.msg}</div>
          <div className="flex flex-col sm:flex-row gap-2">
            <a
              href={state.url}
              download={filename}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 font-semibold"
              style={{
                background: "#0C2A40",
                color: "#FFFFFF",
                fontSize: "0.78rem",
                letterSpacing: "0.10em",
                textTransform: "uppercase",
                borderRadius: 4,
              }}
              data-testid={`resource-download-${r.slug}`}
            >
              <FileDown size={14} aria-hidden /> Download PDF
            </a>
            <a
              href={state.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 font-semibold"
              style={{
                background: "transparent",
                color: "#0C2A40",
                border: "1px solid rgba(12,42,64,0.30)",
                fontSize: "0.78rem",
                letterSpacing: "0.10em",
                textTransform: "uppercase",
                borderRadius: 4,
              }}
              data-testid={`resource-view-${r.slug}`}
            >
              Open in browser
            </a>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} noValidate className="flex flex-col sm:flex-row gap-2" data-testid={`resource-form-${r.slug}`}>
          <input
            type="email"
            required
            autoComplete="email"
            placeholder="you@yourlodge.co.za"
            value={email}
            onChange={(e) => { setEmail(e.target.value); if (state.status === "error") setState({ status: "idle", msg: "" }); }}
            className="flex-1 px-4 py-2.5 text-[0.9rem] font-light outline-none"
            style={{
              background: "#FAFAF7",
              border: `1px solid ${state.status === "error" ? "#D04A4A" : "rgba(0,0,0,0.10)"}`,
              color: "#0C2A40",
              borderRadius: 4,
            }}
            data-testid={`resource-email-${r.slug}`}
          />
          <button
            type="submit"
            disabled={state.status === "loading"}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 font-semibold disabled:opacity-60"
            style={{
              background: "#1FB6C1",
              color: "#070A0D",
              fontSize: "0.74rem",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              borderRadius: 4,
            }}
            data-testid={`resource-submit-${r.slug}`}
          >
            {state.status === "loading" ? "Sending…" : <>Get the PDF <ArrowRight size={13} aria-hidden /></>}
          </button>
        </form>
      )}
      {state.status === "error" && (
        <p className="mt-2 text-[0.76rem]" style={{ color: "#D04A4A" }} data-testid={`resource-error-${r.slug}`}>
          {state.msg}
        </p>
      )}
      <div className="mt-3 flex flex-wrap gap-1.5">
        {r.tags.map((t) => (
          <span
            key={t}
            className="inline-flex items-center px-2 py-0.5 text-[0.6rem] font-medium"
            style={{
              background: "rgba(12,42,64,0.06)",
              color: "rgba(12,42,64,0.7)",
              borderRadius: 3,
              letterSpacing: "0.04em",
            }}
          >
            {t}
          </span>
        ))}
      </div>
    </div>
  );
}


// ─── Video card ──────────────────────────────────────────────────────────────
//
// Renders YouTube + self-hosted clips identically. The actual playback
// happens in the universal <VideoLightbox/> mounted at the page root —
// this card just shows the poster + play affordance and triggers onPlay.
//
function VideoCard({ video: v, onPlay }) {
  const isPortrait = v.orientation === "portrait";
  const isYouTube = !!v.youtube_id;
  // YouTube auto-thumbnail when no poster is supplied (hqdefault works
  // for both Shorts and regular uploads; ~480×360 4:3 with letterbox).
  const poster =
    v.poster ||
    (isYouTube ? `https://i.ytimg.com/vi/${v.youtube_id}/hqdefault.jpg` : "/library/purification/purification_01.webp");

  return (
    <article className="group block" data-testid={`video-card-${v.slug}`}>
      <button
        type="button"
        onClick={onPlay}
        className="relative w-full overflow-hidden cursor-pointer text-left block"
        style={{
          background: "#0C2A40",
          borderRadius: 4,
          aspectRatio: isPortrait ? "9 / 16" : "16 / 9",
        }}
        aria-label={`Play video: ${v.title}`}
      >
        <img
          src={poster}
          alt=""
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          onError={(e) => { e.currentTarget.src = "/library/purification/purification_01.webp"; }}
        />
        {/* Centre play button */}
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{ background: "linear-gradient(180deg, rgba(7,10,13,0.05) 0%, rgba(7,10,13,0.55) 100%)" }}
        >
          <div
            className="flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
            style={{
              width: 40,
              height: 40,
              borderRadius: 999,
              background: "rgba(255,255,255,0.96)",
              boxShadow: "0 4px 14px rgba(0,0,0,0.30)",
            }}
            data-testid={`video-play-${v.slug}`}
          >
            <Play size={14} fill="#0C2A40" color="#0C2A40" style={{ marginLeft: 2 }} aria-hidden />
          </div>
        </div>
        {/* Duration badge */}
        {v.duration && (
          <div
            className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 inline-flex items-center gap-1 text-[0.6rem] font-semibold"
            style={{
              background: "rgba(7,10,13,0.85)",
              color: "#F5F7FA",
              borderRadius: 3,
              letterSpacing: "0.02em",
            }}
          >
            <Clock size={8} aria-hidden /> {v.duration}
          </div>
        )}
        {/* Video type chip — top-left */}
        <div
          className="absolute top-1.5 left-1.5 px-1.5 py-0.5 inline-flex items-center gap-1 text-[0.54rem] font-semibold"
          style={{
            background: "rgba(31,182,193,0.95)",
            color: "#070A0D",
            borderRadius: 3,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
          }}
        >
          <Video size={8} aria-hidden /> {isPortrait ? "Short" : "Field"}
        </div>
      </button>

      <div
        className="mt-2 flex items-center gap-1.5 text-[0.7rem] font-medium"
        style={{ color: "rgba(12,42,64,0.55)" }}
      >
        <Calendar size={10} aria-hidden /> {fmtDate(v.date)}
      </div>
      <h3
        className="font-display mt-1 group-hover:text-[#1FB6C1] transition-colors"
        style={{
          fontSize: "0.88rem",
          lineHeight: 1.3,
          letterSpacing: "-0.005em",
          color: "#0C2A40",
          fontWeight: 600,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {v.title}
      </h3>
      {v.filmed_at && (
        <div
          className="mt-1 inline-flex items-center gap-1 text-[0.68rem] font-medium"
          style={{ color: "rgba(12,42,64,0.55)" }}
        >
          {v.project_slug ? (
            <Link
              to={`/projects/${v.project_slug}`}
              className="inline-flex items-center gap-1 hover:text-[#1FB6C1] transition-colors"
              data-testid={`video-project-link-${v.slug}`}
              onClick={(e) => e.stopPropagation()}
            >
              <MapPin size={10} aria-hidden /> {v.filmed_at}
            </Link>
          ) : (
            <span className="inline-flex items-center gap-1">
              <MapPin size={10} aria-hidden /> {v.filmed_at}
            </span>
          )}
        </div>
      )}
      <p className="mt-1.5 font-light text-[0.78rem] leading-snug text-black/65">
        {v.caption}
      </p>
      {v.tags && v.tags.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {v.tags.slice(0, 2).map((t) => (
            <span
              key={t}
              className="inline-flex items-center px-1.5 py-0.5 text-[0.56rem] font-medium"
              style={{
                background: "rgba(12,42,64,0.06)",
                color: "rgba(12,42,64,0.7)",
                borderRadius: 3,
                letterSpacing: "0.04em",
              }}
            >
              {t}
            </span>
          ))}
        </div>
      )}
    </article>
  );
}

// ─── Empty state (shown when no videos are published yet) ────────────────────
function VideosEmptyState() {
  return (
    <div
      className="grid md:grid-cols-2 gap-8 items-center py-10 md:py-14"
      data-testid="insights-videos-empty-state"
    >
      <div
        className="aspect-video flex flex-col items-center justify-center p-8 text-center"
        style={{
          background: "linear-gradient(135deg, #0C2A40 0%, #112E45 50%, #1F2227 100%)",
          color: "#F5F7FA",
          borderRadius: 4,
        }}
      >
        <div
          className="inline-flex items-center justify-center mb-4"
          style={{
            width: 64, height: 64, borderRadius: 999,
            background: "rgba(31,182,193,0.18)",
            border: "1px solid rgba(31,182,193,0.45)",
          }}
        >
          <Video size={26} color="#1FB6C1" aria-hidden />
        </div>
        <div
          className="text-[0.72rem] font-semibold mb-2"
          style={{ color: "#1FB6C1", letterSpacing: "0.18em", textTransform: "uppercase" }}
        >
          From the field
        </div>
        <div className="text-[1.15rem] font-semibold leading-snug max-w-[26ch]">
          Field videos are filmed when filming is honest, not before.
        </div>
      </div>
      <div>
        <h3
          className="font-display mb-3"
          style={{ fontSize: "1.35rem", lineHeight: 1.25, color: "#0C2A40", fontWeight: 600 }}
        >
          Site walk-throughs, install reels and customer briefs — coming.
        </h3>
        <p className="font-light text-[0.96rem] leading-relaxed text-black/70 mb-4">
          We film inside plant rooms, on borehole heads, and in the back of bakkies — not in studios.
          Every clip is shot on a live HidroVerse site, with the actual team on the actual brief.
          The first batch lands once the cameras start rolling.
        </p>
        <p className="font-light text-[0.92rem] leading-relaxed text-black/55 mb-6">
          In the meantime, the engineering depth lives in the <Link to="/insights" className="underline underline-offset-2" style={{ color: "#1FB6C1" }}>articles</Link> and the
          downloadable <button
            type="button"
            className="underline underline-offset-2 cursor-pointer"
            style={{ color: "#1FB6C1", background: "transparent", border: 0, padding: 0 }}
            onClick={() => { /* tab parent — let user click Downloads tab directly */ }}
          >procurement PDFs</button>.
        </p>
        <Link
          to="/contact"
          className="inline-flex items-center gap-2 px-5 py-2.5 font-semibold transition-colors"
          style={{
            background: "#0C2A40",
            color: "#FFFFFF",
            fontSize: "0.78rem",
            letterSpacing: "0.10em",
            textTransform: "uppercase",
            borderRadius: 4,
          }}
          data-testid="videos-empty-cta"
        >
          Book a site visit instead <ArrowRight size={14} aria-hidden />
        </Link>
      </div>
    </div>
  );
}
